import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import QRCode from 'qrcode';
import { useParams, useLocation } from 'react-router-dom';
import { reportingApi, devFilesApi, API_BASE_URL } from '../../services/api';
import { getReportMeta } from './config/reportRegistry';
import { useNotifications } from '../../context/NotificationContext';
import { useActivityTracker } from '../../context/ActivityTrackerContext';
import ReportParameterCombobox from './components/ReportParameterCombobox';
import ReportToolbar from './components/ReportToolbar';
import ReportPaginator from './components/ReportPaginator';
import { FileSpreadsheet, CheckCircle, XCircle, MinusCircle, ChevronDown, ChevronRight } from 'lucide-react';
import wmLogo from '../../assets/logos/WM-Logo-Full.png';
import wmLogoCb from '../../assets/WM-Logo-CB-Transparent-White5.png';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import exportReportPdf from './export/reportPdfExporter';
import exportReportExcel from './export/reportExcelExporter';
import exportReportCsv from './export/reportCsvExporter';
import buildReportFilename from './export/reportFilename';

// Mirrors backend ToCamelCase: all-uppercase names (e.g. "DTC") go fully lowercase,
// others just lower the first character.
const toCamelCase = (name) => {
  if (!name) return name;
  if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
  return name.charAt(0).toLowerCase() + name.slice(1);
};

// Canvas-based text measurement for fixed header column sizing
const _mc = document.createElement('canvas').getContext('2d');
const measureTextPx = (text, font) => { _mc.font = font; return _mc.measureText(String(text)).width; };

// Split a multi-word column header into at most 2 display lines.
// 1 word → [label, ''] | 2 words → [word1, word2] | 3+ → optimal split minimizing max line length
const splitHeaderLabel = (label) => {
  const words = label.split(' ');
  if (words.length <= 1) return [label, ''];
  if (words.length === 2) return [words[0], words[1]];
  let bestSplit = 1, bestMax = Infinity;
  for (let i = 1; i < words.length; i++) {
    const m = Math.max(words.slice(0, i).join(' ').length, words.slice(i).join(' ').length);
    if (m < bestMax) { bestMax = m; bestSplit = i; }
  }
  return [words.slice(0, bestSplit).join(' '), words.slice(bestSplit).join(' ')];
};

// Reports that always group by a fixed field — grouping selector is locked for these.
const FIXED_GROUP_BY = {
  'slugRptStd-Sub-Index_Report_Panels': 'Index_Sheet',
  'slugRptStd-Sub-Index_Report_Tags':   'Index_Sheet',
};

// Columns hidden from preview/PDF for Index reports (group field + spec-label columns)
const INDEX_REPORT_HIDDEN_FIELDS = new Set([
  'Index_Sheet', 'IDX_SPEC_1_COL', 'IDX_SPEC_2_COL',
  'IDX_SPEC_3_COL', 'IDX_SPEC_4_COL', 'IDX_SPEC_5_COL',
]);
// Maps each spec-label field to the spec data field whose header it drives
const INDEX_SPEC_COL_MAP = [
  ['IDX_SPEC_1_COL', 'IDX_SPEC_1'],
  ['IDX_SPEC_2_COL', 'IDX_SPEC_2'],
  ['IDX_SPEC_3_COL', 'IDX_SPEC_3'],
  ['IDX_SPEC_4_COL', 'IDX_SPEC_4'],
  ['IDX_SPEC_5_COL', 'IDX_SPEC_5'],
];
// Returns cols with each Spec N column's label replaced by the IDX_SPEC_N_COL value in firstRow.
// IDX_SPEC_N_COL has fkSourceField so the backend only returns it as {camel}_Display, not raw.
const applyIndexSpecLabels = (cols, firstRow) =>
  cols.map(c => {
    const entry = INDEX_SPEC_COL_MAP.find(([, sf]) => sf === c.field);
    if (!entry || !firstRow) return c;
    const camelColField = toCamelCase(entry[0]);
    const label = firstRow[`${camelColField}_Display`] || firstRow[camelColField] || c.label;
    return { ...c, label };
  });

const ReportViewerPage = () => {
  const { reportSlug, dbSlug } = useParams();
  const location = useLocation();
  const { notify } = useNotifications();
  const { registerActivity, updateActivity, completeActivity, failActivity } = useActivityTracker();
  const dbContext = useDatabaseSafe();
  const dbName = localStorage.getItem('pmd-selected-database') || 'DB';

  const [config, setConfig] = useState(null);
  const [clientLogoSrc, setClientLogoSrc] = useState(null);
  const reportDisplayName = getReportMeta(reportSlug)?.label || config?.reportName || '';
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [executionMs, setExecutionMs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [parameters, setParameters] = useState({});
  const [committedParameters, setCommittedParameters] = useState({});
  const [hasRun, setHasRun] = useState(false);
  const [lastRunParams, setLastRunParams] = useState(null);

  // Sort state
  const [sortOrder, setSortOrder] = useState([]);
  const [defaultSort, setDefaultSort] = useState([]);
  const [isDefaultSort, setIsDefaultSort] = useState(true);

  // Pagination state — page size derived from physical PDF page dimensions
  const [currentPage, setCurrentPage] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [selectedPageSize, setSelectedPageSize] = useState(null); // null = use config default

  // Fixed pixel widths for parameter header columns — computed once from all possible values
  const [paramHeaderWidths, setParamHeaderWidths] = useState({});
  // Pre-fetched options per field — passed to comboboxes so they render at correct width immediately
  const [paramOptions, setParamOptions] = useState({});
  // Gate: don't render comboboxes until param options are fetched (prevents resize jump on load)
  const [paramOptionsReady, setParamOptionsReady] = useState(false);

  // Export grouping state
  const [exportGroupBy, setExportGroupBy] = useState(null);
  const [exportGroupSeparate, setExportGroupSeparate] = useState(false);

  // Report options
  const [showQrCode, setShowQrCode] = useState(false);

  // QR code data URL — generated from the current report URL + encoded parameters
  const [qrDataUrl, setQrDataUrl] = useState(null);

  // DAWN version string — fetched once, displayed in report footer
  const [dawnVersion, setDawnVersion] = useState('');
  useEffect(() => {
    devFilesApi.getRevLog()
      .then(res => setDawnVersion(res.data?.currentVersion || ''))
      .catch(() => {});
  }, []);

  // Tracks which reportSlug the currently loaded config belongs to — prevents stale-config
  // param fetches when navigating report-to-report (sibling effects see pre-setState values)
  const configSlugRef = useRef(null);
  // Holds compact {field:[id]} map from ?p= QR URL — resolved after options load
  const urlParamsRef = useRef(null);

  // Scale entire preview (header + table + footer) as one unit — zoom-independent
  const previewContainerRef = useRef(null);
  const previewContentRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);

  // Load report config from API
  useEffect(() => {
    if (!reportSlug) return;

    const meta = getReportMeta(reportSlug);
    if (!meta) {
      notify.error(`Unknown report: ${reportSlug}`, 'error');
      setConfigLoading(false);
      return;
    }

    // Reset all run state when navigating to a different report
    setData([]);
    setTotalCount(0);
    setExecutionMs(null);
    setHasRun(false);
    setLastRunParams(null);
    setCurrentPage(1);
    setConfig(null);
    setExportGroupBy(null);
    setExportGroupSeparate(false);
    setShowQrCode(false);
    setCommittedParameters({});
    configSlugRef.current = null;
    urlParamsRef.current = null;
    setParamHeaderWidths({});
    setParamOptions({});
    setParamOptionsReady(false);

    setConfigLoading(true);
    reportingApi.getConfig(meta.configId)
      .then(res => {
        configSlugRef.current = reportSlug;
        const PARAM_ORDER = {
          Source: 1, Drawing: 2, Network: 3, Parent: 4,
          Panel: 5, Subnet: 5, Equipment: 6, Index: 7, Tag: 8, 'Port Type': 9
        };
        const sortedConfig = {
          ...res.data,
          parameters: [...(res.data.parameters || [])].sort((a, b) => {
            const oa = PARAM_ORDER[a.field] ?? PARAM_ORDER[a.label] ?? 99;
            const ob = PARAM_ORDER[b.field] ?? PARAM_ORDER[b.label] ?? 99;
            return oa - ob;
          })
        };
        setConfig(sortedConfig);
        // Force fixed grouping for reports that always group by a specific field
        if (FIXED_GROUP_BY[reportSlug]) setExportGroupBy(FIXED_GROUP_BY[reportSlug]);
        const savedParams = location?.state?.params || null;

        // If the URL contains ?p=<base64>, stash the compact {field:[id]} map in a ref.
        // Actual reconstruction happens after paramOptions load (we need display names).
        try {
          const pParam = new URLSearchParams(window.location.search).get('p');
          if (pParam) urlParamsRef.current = JSON.parse(atob(pParam));
        } catch (_) { /* ignore malformed */ }

        // Start with defaults (or router state); QR params override after options load
        const initialParams = {};
        (res.data.parameters || []).forEach(p => {
          initialParams[p.field] = savedParams?.[p.field]
            ?? [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
        });
        setParameters(initialParams);

        const configSort = (res.data.sorting || []).map(s => ({
          field: s.field,
          direction: (s.direction || 'asc').toLowerCase()
        }));
        setDefaultSort(configSort);
        setSortOrder(configSort);
        setIsDefaultSort(true);
      })
      .catch(err => {
        notify.error(`Failed to load report config: ${err.message}`, 'error');
      })
      .finally(() => setConfigLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSlug]);

  // Fetch client logo and convert to base64 for PDF (worker needs serialisable data, not a URL).
  // Goes through /api/proxy/image to avoid CORS on the Azure blob storage origin.
  // Preview uses iconUrl directly — no fetch needed there.
  const clientIconUrl = dbContext?.currentDb?.iconUrl || null;
  const clientName = dbContext?.currentDb?.description || dbContext?.currentDb?.displayName || '';
  useEffect(() => {
    if (!clientIconUrl) { setClientLogoSrc(null); return; }
    const proxyUrl = `${API_BASE_URL}/api/proxy/image?url=${encodeURIComponent(clientIconUrl)}`;
    fetch(proxyUrl)
      .then(r => r.blob())
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .then(dataUrl => setClientLogoSrc(dataUrl))
      .catch(() => setClientLogoSrc(null));
  }, [clientIconUrl]);

  // Auto-export trigger — when navigated from Activity History "Re-download"
  const autoExportTriggeredRef = useRef(false);
  useEffect(() => {
    const state = location?.state;
    if (!state?.autoExport || !config || autoExportTriggeredRef.current) return;
    autoExportTriggeredRef.current = true;
    const doAutoExport = async () => {
      await new Promise(r => setTimeout(r, 300));
      await runReport(state.params || undefined);
      await new Promise(r => setTimeout(r, 500));
      // eslint-disable-next-line no-use-before-define
      if (state.autoExport === 'pdf')   handleExportPdf();
      // eslint-disable-next-line no-use-before-define
      if (state.autoExport === 'excel') handleExportExcel();
      // eslint-disable-next-line no-use-before-define
      if (state.autoExport === 'csv')   handleExportCsv();
    };
    doAutoExport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, location?.state]);

  // After config loads, fetch all param options and compute fixed column widths.
  // Guard: configSlugRef.current must match reportSlug — prevents stale-config fetches
  // when navigating report-to-report (React batches sibling effect state updates, so
  // config still holds the previous report's value when this effect fires after slug change).
  useEffect(() => {
    if (!config || !reportSlug) return;
    if (configSlugRef.current !== reportSlug) return;
    const params = config.parameters || [];
    if (params.length === 0) return;

    Promise.all(
      params.map(p =>
        reportingApi.getParameterOptions(reportSlug, p.field, null)
          .then(res => ({ field: p.field, label: p.label, options: res.data || [] }))
          .catch(() => ({ field: p.field, label: p.label, options: [] }))
      )
    ).then(results => {
      const widths = {};
      results.forEach(({ field, label, options }) => {
        // Size column by character count, capped at 150px. 1 char ≈ 1px.
        // For fullDisplay fields (headerDisplayFormat concatenations like "NUM - DESC"),
        // parse on " - " and track prefix/suffix lengths independently so that the
        // worst-case combination (longest number from one row + longest desc from another)
        // is used rather than a single row's total length.
        const hasFullDisplay = options.some(o => o.fullDisplay);
        let maxChars = label.length;
        if (hasFullDisplay) {
          let maxPrefixLen = 0, maxSuffixLen = 0;
          options.forEach(o => {
            const fd = String(o.fullDisplay || o.display || '');
            const sepIdx = fd.indexOf(' - ');
            if (sepIdx >= 0) {
              maxPrefixLen = Math.max(maxPrefixLen, sepIdx);
              maxSuffixLen = Math.max(maxSuffixLen, fd.length - sepIdx - 3);
            } else {
              maxPrefixLen = Math.max(maxPrefixLen, fd.length);
            }
          });
          maxChars = Math.max(maxChars, maxPrefixLen + (maxSuffixLen > 0 ? 3 + Math.min(maxSuffixLen, 70) : 0));
        } else {
          options.forEach(o => {
            maxChars = Math.max(maxChars, String(o.display || '').length);
          });
        }
        // Store as PDF pt directly (4pt Helvetica × 0.625 char ratio = 2.5pt/char, +6 = paddingLeft).
        // 0.625 calibrated for uppercase engineering labels (panel IDs, tag names, etc).
        // getW() in ReportPdfDocument uses these as-is. Preview uses them as approximate px (clips OK).
        widths[field] = Math.max(40, Math.min(Math.round(maxChars * 2.5) + 6, 310));
      });
      setParamHeaderWidths(widths);
      const optMap = {};
      results.forEach(({ field, options }) => { optMap[field] = options; });
      setParamOptions(optMap);
      setParamOptionsReady(true);

      // Resolve QR ?p= compact params now that we have display names
      if (urlParamsRef.current) {
        const compact = urlParamsRef.current;
        urlParamsRef.current = null;
        const resolved = {};
        (config.parameters || []).forEach(p => {
          const ids = compact[p.field];
          if (ids && optMap[p.field]) {
            const matched = optMap[p.field].filter(opt =>
              ids.includes(String(opt.id ?? opt.value ?? ''))
            );
            resolved[p.field] = matched.length > 0
              ? matched.map(opt => ({
                  type: 'item',
                  id: opt.id ?? opt.value,
                  display: opt.display,
                  fullDisplay: opt.fullDisplay || opt.display,
                  operator: 'eq',
                  value: opt.value ?? opt.id
                }))
              : [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
          } else {
            resolved[p.field] = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
          }
        });
        setParameters(resolved);
      }
    });

    // No params — ready immediately
    if (params.length === 0) setParamOptionsReady(true);
  }, [config, reportSlug]);

  // Check if current sort matches default
  const checkIsDefault = useCallback((sort) => {
    if (sort.length !== defaultSort.length) return false;
    return sort.every((s, i) =>
      s.field === defaultSort[i].field && s.direction === defaultSort[i].direction
    );
  }, [defaultSort]);

  // Handle column header click for sorting
  const handleSortClick = useCallback((field, e) => {
    setSortOrder(prev => {
      let newSort;
      const existingIndex = prev.findIndex(s => s.field === field);

      if (e.shiftKey) {
        if (existingIndex >= 0) {
          newSort = [...prev];
          if (newSort[existingIndex].direction === 'asc') {
            newSort[existingIndex] = { field, direction: 'desc' };
          } else {
            newSort.splice(existingIndex, 1);
          }
        } else {
          newSort = [...prev, { field, direction: 'asc' }];
        }
      } else {
        if (existingIndex >= 0 && prev.length === 1) {
          if (prev[0].direction === 'asc') {
            newSort = [{ field, direction: 'desc' }];
          } else {
            newSort = [];
          }
        } else {
          newSort = [{ field, direction: 'asc' }];
        }
      }

      setIsDefaultSort(checkIsDefault(newSort));
      return newSort;
    });
    setCurrentPage(1);
  }, [checkIsDefault]);

  // Reset to default sort
  const resetSort = useCallback(() => {
    setSortOrder(defaultSort);
    setIsDefaultSort(true);
    setCurrentPage(1);
  }, [defaultSort]);

  // Sort data client-side
  const sortedData = useMemo(() => {
    if (sortOrder.length === 0) return data;

    return [...data].sort((a, b) => {
      for (const { field, direction } of sortOrder) {
        const camelField = toCamelCase(field);
        const displayKey = `${camelField}_Display`;
        const aVal = a[displayKey] != null ? a[displayKey] : a[camelField];
        const bVal = b[displayKey] != null ? b[displayKey] : b[camelField];

        if (aVal == null && bVal == null) continue;
        if (aVal == null) return direction === 'asc' ? 1 : -1;
        if (bVal == null) return direction === 'asc' ? -1 : 1;

        let cmp;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          cmp = aVal - bVal;
        } else {
          cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
        }

        if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [data, sortOrder]);

  const exportGroupCount = useMemo(() => {
    if (!exportGroupBy || !sortedData.length) return 0;
    const cf = toCamelCase(exportGroupBy);
    const seen = new Set();
    sortedData.forEach(row => {
      const val = row[`${cf}_Display`] ?? row[cf];
      seen.add(val != null ? String(val) : '(Blank)');
    });
    return seen.size;
  }, [exportGroupBy, sortedData]);

  // Pre-compute combobox container widths from fetched options — same formula as inside the combobox
  // This is passed as initialWidth so the container is never undersized on first paint
  const paramComboboxWidths = useMemo(() => {
    const result = {};
    Object.entries(paramOptions).forEach(([field, opts]) => {
      if (!opts.length) { result[field] = 180; return; }
      const longest = opts.reduce((acc, o) => {
        const d = String(o.display || '');
        return d.length > acc.length ? d : acc;
      }, '');
      const textWidth = measureTextPx(longest, '13px sans-serif');
      result[field] = Math.max(180, Math.min(Math.ceil(textWidth) + 64, 500));
    });
    return result;
  }, [paramOptions]);

  // Column widths for preview — same content-aware algorithm as PDF export so
  // the preview table layout matches the PDF exactly (percentages of 1176pt content area).
  const PDF_CONTENT_WIDTH_PT = 1176; // TABLOID landscape 1224pt minus 24pt × 2 horizontal padding
  const previewColWidths = useMemo(() => {
    if (!config || !sortedData.length) return {};
    const isIndexRpt = !!FIXED_GROUP_BY[reportSlug];
    const allCols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    // Index reports: exclude the group field and the spec-label columns from the width table
    const cols = isIndexRpt ? allCols.filter(c => !INDEX_REPORT_HIDDEN_FIELDS.has(c.field)) : allCols;
    const fontSize = reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 6 : (config.layout?.fontSize || 8);
    const dataFont = `${fontSize}px Helvetica, sans-serif`;
    const headerFont = `bold ${fontSize}px Helvetica, sans-serif`;
    const CELL_PAD_PT = 4;
    const WRAPPABLE_FIELDS = new Set(['Note', 'Comment']);
    const minWidths = {};
    cols.forEach(c => {
      const camelField = toCamelCase(c.field);
      const displayKey = `${camelField}_Display`;
      const isWrappable = WRAPPABLE_FIELDS.has(c.field);
      let maxDataPx = 0;
      if (!isWrappable) {
        sortedData.forEach(row => {
          const val = row[displayKey] != null ? row[displayKey] : row[camelField];
          if (val != null) maxDataPx = Math.max(maxDataPx, measureTextPx(String(val), dataFont));
        });
      }
      // For Index report spec columns, header label = IDX_SPEC_N_COL value (varies per group).
      // Measure the widest label across all rows so the column fits any group's header.
      let headerPx;
      if (isIndexRpt) {
        const specEntry = INDEX_SPEC_COL_MAP.find(([, sf]) => sf === c.field);
        if (specEntry) {
          // fkSourceField columns come back as {camel}_Display — check that key first
          const camelColField = toCamelCase(specEntry[0]);
          let maxLabelPx = 0;
          sortedData.forEach(row => {
            const lbl = row[`${camelColField}_Display`] || row[camelColField];
            if (lbl) maxLabelPx = Math.max(maxLabelPx, measureTextPx(String(lbl), headerFont));
          });
          headerPx = maxLabelPx || measureTextPx(c.label, headerFont);
        }
      }
      if (headerPx == null) {
        const [hLine1, hLine2] = splitHeaderLabel(c.label);
        headerPx = Math.max(measureTextPx(hLine1, headerFont), hLine2 ? measureTextPx(hLine2, headerFont) : 0);
      }
      // IO Assignments uses 6pt font and was calibrated at 0.75 — leave it alone.
      // All other reports use 8pt font: PDF renders at fontSize pt but we measure at fontSize px
      // (CSS px ≈ 0.75pt), so measured width is ~25% too narrow. Use 1.0 to avoid clipping.
      const ptFactor = reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 0.75 : 1.0;
      minWidths[c.field] = Math.max(67, Math.ceil(Math.max(maxDataPx, headerPx) * ptFactor) + CELL_PAD_PT);
    });
    // Wrappable column floors: Note ≥ Description width; Comment ≥ 333px
    if (minWidths.Note != null) {
      minWidths.Note = Math.max(minWidths.Note, minWidths.Description || minWidths.Tag_Description || 67);
    }
    if (minWidths.Comment != null) {
      minWidths.Comment = Math.max(minWidths.Comment, 233);
    }
    const totalMinPt = cols.reduce((sum, c) => sum + minWidths[c.field], 0);
    const colWidths = {};
    if (totalMinPt >= PDF_CONTENT_WIDTH_PT) {
      cols.forEach(c => { colWidths[c.field] = `${((minWidths[c.field] / totalMinPt) * 100).toFixed(1)}%`; });
    } else {
      const remaining = PDF_CONTENT_WIDTH_PT - totalMinPt;
      const totalConfigPx = cols.reduce((sum, c) => sum + (c.pixelWidth || c.width * 10 || 100), 0);
      cols.forEach(c => {
        const configShare = (c.pixelWidth || c.width * 10 || 100) / totalConfigPx;
        const extraPt = remaining * configShare;
        colWidths[c.field] = `${(((minWidths[c.field] + extraPt) / PDF_CONTENT_WIDTH_PT) * 100).toFixed(1)}%`;
      });
    }
    return colWidths;
  }, [config, sortedData, reportSlug]);

  // URL embedded in the QR code — compact {field:[id]} map, skipping __all__ defaults.
  // Stripping display names and default fields keeps the payload tiny so the QR code
  // stays at a low version (sparse pattern = easier to scan and print at low DPI).
  const reportUrl = useMemo(() => {
    if (!hasRun || !config) return null;
    const compact = {};
    Object.entries(parameters).forEach(([field, sels]) => {
      // Skip fields where the only selection is the "All" default
      if (sels.length === 1 && sels[0].id === '__all__') return;
      compact[field] = sels.map(s => String(s.id));
    });
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(compact))));
    return `${window.location.origin}${window.location.pathname}?p=${encoded}`;
  }, [hasRun, config, parameters]);

  // Regenerate QR data URL whenever the report URL changes
  useEffect(() => {
    if (!reportUrl) { setQrDataUrl(null); return; }
    QRCode.toDataURL(reportUrl, {
      width: 400, margin: 2, // 400px source — prints cleanly at 300dpi up to ~1.3"
      color: { dark: '#1a5568', light: '#ffffff' }
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [reportUrl]);

  // Calculate rows per page from actual PDF layout measurements (points)
  const pageSize = useMemo(() => {
    if (!config?.layout) return 47;

    // Page dimensions in points (72pt = 1 inch), landscape orientation
    const pageDimensions = {
      letter:  { width: 792, height: 612 },   // 11x8.5
      tabloid: { width: 1224, height: 792 }    // 17x11
    };

    const dims = pageDimensions[config.layout.defaultPageSize] || pageDimensions.tabloid;

    // Fixed measurements matching ReportPdfStyles.js exactly
    const pageMarginTop = 30;
    const pageMarginBottom = 40;
    const headerHeight = 115;     // worst-case: 2 param rows × maxHeight(48) + margins/border(19)
    const colHeaderHeight = 16;   // paddingV(8) + fontSize(6.5) + border(1.5)
    const fontSize = config.layout.fontSize || 8;
    const rowHeight = fontSize + 5.5;  // fontSize + paddingV(2.5×2) + border(0.5)

    const usableHeight = dims.height - pageMarginTop - pageMarginBottom - headerHeight - colHeaderHeight;
    const rowsPerPage = Math.floor(usableHeight / rowHeight);

    const calculated = Math.max(10, rowsPerPage);
    return Math.min(calculated, 40);
  }, [config]);

  // Grouped page descriptors — built when Group By is active
  const groupedPages = useMemo(() => {
    if (!exportGroupBy || !hasRun || !sortedData.length) return null;
    const cf = toCamelCase(exportGroupBy);
    const displayKey = `${cf}_Display`;
    const groupColLabel = config?.parameters?.find(p => p.field === exportGroupBy)?.label
      || config?.columns?.find(c => c.field === exportGroupBy)?.label
      || exportGroupBy;
    const groupMap = new Map();
    sortedData.forEach(row => {
      const val = row[displayKey] != null ? row[displayKey] : row[cf];
      const key = val != null ? String(val) : '(Blank)';
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key).push(row);
    });
    const pages = [];
    [...groupMap.entries()]
      .sort(([a], [b]) => {
        if (a === '(Blank)') return 1;
        if (b === '(Blank)') return -1;
        return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
      })
      .forEach(([groupVal, groupRows]) => {
        const subtitle = `(${groupColLabel}: ${groupVal})`;
        for (let i = 0; i < Math.max(groupRows.length, 1); i += pageSize) {
          pages.push({ subtitle, groupRowCount: groupRows.length, rows: groupRows.slice(i, i + pageSize) });
        }
      });
    return pages.length > 0 ? pages : null;
  }, [exportGroupBy, sortedData, pageSize, config, hasRun]);

  // Paginate sorted data
  const totalPages = groupedPages
    ? groupedPages.length
    : Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = useMemo(() => {
    if (groupedPages) return groupedPages[currentPage - 1]?.rows || [];
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [groupedPages, sortedData, currentPage, pageSize]);

  const hasOverLimit = Object.values(parameters).some(s => s?.length > 4);

  // Run report
  const runReport = useCallback(async (overrideParams) => {
    if (!config) return;
    if (hasOverLimit) {
      notify.warning('One or more parameters exceed the 4-value limit. Reduce selections before running the report.');
      return;
    }

    setLoading(true);
    try {
      const paramPayload = {};
      Object.entries(overrideParams ?? parameters).forEach(([field, selections]) => {
        const camelField = toCamelCase(field);
        // "All" means no filter on this field — exclude from payload entirely
        const filtered = (selections || []).filter(s => s.id !== '__all__');
        if (filtered.length > 0) {
          paramPayload[camelField] = filtered.map(s => ({
            operator: s.operator,
            value: s.value
          }));
        }
      });

      const res = await reportingApi.runReport(reportSlug, { parameters: paramPayload });
      setData(res.data.data);
      setTotalCount(res.data.totalCount);
      setExecutionMs(res.data.executionMs);
      setHasRun(true);
      setCommittedParameters({ ...parameters });
      setLastRunParams(JSON.stringify(parameters));
      setCurrentPage(1);
    } catch (err) {
      notify.error(`Report failed: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [config, parameters, notify, hasOverLimit, reportSlug]);

  // Recalculate preview scale whenever data/page/zoom changes.
  // Only active after a report has been run — pre-run the header is a flex layout
  // with no minimum width anchor so scale thrashes. Lock to 1 until data is loaded.
  useLayoutEffect(() => {
    if (!hasRun) {
      setPreviewScale(1);
      return;
    }

    const calcScale = () => {
      if (!previewContainerRef.current || !previewContentRef.current) return;
      // Measure natural content width directly — the width compensation formula
      // (width: 100/previewScale %) means scrollWidth already equals natural width,
      // so no need to reset to scale=1 first (that reset caused a visible flash).
      requestAnimationFrame(() => {
        if (!previewContainerRef.current || !previewContentRef.current) return;
        const containerWidth = previewContainerRef.current.clientWidth;
        const contentWidth = previewContentRef.current.scrollWidth;
        setPreviewScale(contentWidth > containerWidth ? containerWidth / contentWidth : 1);
      });
    };

    calcScale();
    window.addEventListener('resize', calcScale);
    return () => window.removeEventListener('resize', calcScale);
  }, [paginatedData, hasRun]);

  // Reset to page 1 when grouping selection changes
  useEffect(() => { setCurrentPage(1); }, [exportGroupBy]);

  const updateParameter = (field, selections) => {
    setParameters(prev => {
      const updated = { ...prev, [field]: selections };
      const ALL_SELECTION = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
      const hasSpecific = (sels) => (sels || []).some(s => s.type === 'picked' || s.type === 'freetext');
      // For every other parameter: if it has no specific selection, reset it to "All"
      (config?.parameters || []).forEach(p => {
        if (p.field === field) return;
        if (!hasSpecific(prev[p.field])) {
          updated[p.field] = ALL_SELECTION;
        }
      });
      return updated;
    });
  };

  const activeFilterCount = Object.values(parameters).reduce((sum, s) =>
    sum + (s || []).filter(x => x.id !== '__all__').length, 0);
  const isCurrent = hasRun && lastRunParams === JSON.stringify(parameters);
  const [exportingType, setExportingType] = useState(null);
  const [exportProgress, setExportProgress] = useState(0);

  // Section collapse state — all expanded by default
  const [sec1Collapsed, setSec1Collapsed] = useState(false);
  const [sec2Collapsed, setSec2Collapsed] = useState(false);
  const [sec3Collapsed, setSec3Collapsed] = useState(false);
  const cancelExportRef = useRef(false);
  const activeWorkerRef = useRef(null); // { worker, reject } — set while PDF worker is running
  const exportTimerRef  = useRef(null); // setInterval handle for progress ticking

  const handleCancelExport = useCallback(() => {
    cancelExportRef.current = true;
    if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
    // Immediately terminate the PDF worker if it's running
    if (activeWorkerRef.current) {
      activeWorkerRef.current.worker.terminate();
      activeWorkerRef.current.reject(new DOMException('Export cancelled by user', 'AbortError'));
      activeWorkerRef.current = null;
    }
    setExportingType(null);
    setExportProgress(0);
    notify.info(config ? `${dbName} - ${reportDisplayName} Report - PDF - Cancelled` : 'Export cancelled.', 3000);
  }, [notify, config, dbName, reportDisplayName]);

  // Clear all filters — reset each parameter back to "All"
  const clearAllFilters = useCallback(() => {
    const cleared = {};
    Object.keys(parameters).forEach(field => {
      cleared[field] = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
    });
    setParameters(cleared);
  }, [parameters]);

  // PDF export
  const handleExportPdf = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    cancelExportRef.current = false;

    // ── Timer setup ──────────────────────────────────────────────────────────
    const _start  = Date.now();

    notify.status(`${dbName} - ${reportDisplayName} Report - PDF - Started`, 0);
    // ─────────────────────────────────────────────────────────────────────────

    const isIndexRpt = !!FIXED_GROUP_BY[reportSlug];
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order)
      .filter(c => !isIndexRpt || !INDEX_REPORT_HIDDEN_FIELDS.has(c.field));

    // Content-aware column widths — measure actual data values + header label,
    // then distribute remaining tabloid-landscape space by config proportion.
    // This guarantees every value fits on one line (style guide: no truncation, no 2-line rows).
    const PDF_PAGE_WIDTH_PT = 1176; // tabloid landscape 1224pt - 48pt padding
    const fontSize = reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 6 : (config.layout?.fontSize || 8);
    const dataFont   = `${fontSize}px Helvetica, sans-serif`;
    const headerFont = `bold ${fontSize}px Helvetica, sans-serif`;
    const CELL_PAD_PT = 4; // left+right padding in PDF cells

    // toCamelCase mirrors the PDF renderer's field lookup
    const toCamelCasePdf = (name) => {
      if (!name) return name;
      if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
      return name.charAt(0).toLowerCase() + name.slice(1);
    };

    // Minimum width per column = max(header label width, longest data value width) + padding
    const WRAPPABLE_FIELDS_PDF = new Set(['Note', 'Comment']);
    const minWidths = {};
    cols.forEach(c => {
      const camelField = toCamelCasePdf(c.field);
      const displayKey = `${camelField}_Display`;
      const isWrappable = WRAPPABLE_FIELDS_PDF.has(c.field);
      let maxDataPx = 0;
      if (!isWrappable) {
        sortedData.forEach(row => {
          const val = row[displayKey] != null ? row[displayKey] : row[camelField];
          if (val != null) {
            maxDataPx = Math.max(maxDataPx, measureTextPx(String(val), dataFont));
          }
        });
      }
      // For Index report spec columns, header label = IDX_SPEC_N_COL value (varies per group).
      // Measure widest label across all rows so the column fits any group's header.
      let headerPx;
      if (isIndexRpt) {
        const specEntry = INDEX_SPEC_COL_MAP.find(([, sf]) => sf === c.field);
        if (specEntry) {
          // fkSourceField columns come back as {camel}_Display — check that key first
          const camelColField = toCamelCasePdf(specEntry[0]);
          let maxLabelPx = 0;
          sortedData.forEach(row => {
            const lbl = row[`${camelColField}_Display`] || row[camelColField];
            if (lbl) maxLabelPx = Math.max(maxLabelPx, measureTextPx(String(lbl), headerFont));
          });
          headerPx = maxLabelPx || measureTextPx(c.label, headerFont);
        }
      }
      if (headerPx == null) {
        const [hLine1, hLine2] = splitHeaderLabel(c.label);
        headerPx = Math.max(measureTextPx(hLine1, headerFont), hLine2 ? measureTextPx(hLine2, headerFont) : 0);
      }
      // IO Assignments uses 6pt font and was calibrated at 0.75 — leave it alone.
      // All other reports use 8pt font: PDF renders at fontSize pt but we measure at fontSize px
      // (CSS px ≈ 0.75pt), so measured width is ~25% too narrow. Use 1.0 to avoid clipping.
      const ptFactor = reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 0.75 : 1.0;
      minWidths[c.field] = Math.max(40, Math.ceil(Math.max(maxDataPx, headerPx) * ptFactor) + CELL_PAD_PT);
    });
    // Wrappable column floors: Note ≥ Description width; Comment ≥ 250pt
    if (minWidths.Note != null) {
      minWidths.Note = Math.max(minWidths.Note, minWidths.Description || minWidths.Tag_Description || 50);
    }
    if (minWidths.Comment != null) {
      minWidths.Comment = Math.max(minWidths.Comment, 175);
    }

    const totalMinPt = cols.reduce((sum, c) => sum + minWidths[c.field], 0);
    const colWidths = {};

    if (totalMinPt >= PDF_PAGE_WIDTH_PT) {
      // Mins already fill the page — just use them as-is
      cols.forEach(c => { colWidths[c.field] = `${((minWidths[c.field] / totalMinPt) * 100).toFixed(1)}%`; });
    } else {
      // Distribute remaining space proportionally by config pixelWidth preference
      const remaining = PDF_PAGE_WIDTH_PT - totalMinPt;
      const totalConfigPx = cols.reduce((sum, c) => sum + (c.pixelWidth || c.width * 10 || 100), 0);
      cols.forEach(c => {
        const configShare = (c.pixelWidth || c.width * 10 || 100) / totalConfigPx;
        const extraPt = remaining * configShare;
        colWidths[c.field] = `${(((minWidths[c.field] + extraPt) / PDF_PAGE_WIDTH_PT) * 100).toFixed(1)}%`;
      });
    }

    // For Index reports: build per-group column configs with dynamic spec headers.
    // Combined PDF passes columnsByGroup so ReportPdfDocument can change headers per group page.
    // Separate-files export uses per-group cols directly (no need for columnsByGroup there).
    let columnsByGroup = null;
    if (isIndexRpt && exportGroupBy) {
      const groupCf = toCamelCasePdf(exportGroupBy);
      const firstRowByGroup = {};
      sortedData.forEach(row => {
        const val = row[`${groupCf}_Display`] != null ? row[`${groupCf}_Display`] : row[groupCf];
        const key = val != null ? String(val) : '(Blank)';
        if (!firstRowByGroup[key]) firstRowByGroup[key] = row;
      });
      columnsByGroup = {};
      Object.entries(firstRowByGroup).forEach(([groupVal, firstRow]) => {
        columnsByGroup[groupVal] = applyIndexSpecLabels(cols, firstRow);
      });
    }

    // For Index reports: strip Index_Sheet from header params (shown as subtitle already).
    const pdfHeaderConfig = isIndexRpt
      ? { ...config, parameters: (config.parameters || []).filter(p => p.field !== 'Index_Sheet') }
      : config;

    // ── Activity Tracker registration ────────────────────────────────────────
    const activityName = `${dbName} - ${reportDisplayName} Report`;
    const exportRecipe = {
      reportId:    reportSlug,
      reportName:  reportDisplayName,
      reportPath:  `/reports/${reportSlug}`,
      format:      'pdf',
      filterParams: parameters,
      fileName:    buildReportFilename(reportDisplayName, 'pdf'),
      dbSlug:      dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa',
      dbName:      dbName,
    };
    registerActivity(`pdf-${reportSlug}`, {
      name: activityName, type: 'download', format: 'pdf',
      phase: 'Preparing…', pct: 0,
      restartFn: () => handleExportPdf(),
      exportRecipe,
    });

    // ── Estimated duration + progress timer ─────────────────────────────────
    const pageCount = sortedData.length > 0
      ? Math.ceil(sortedData.length / (reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 40 : 30))
      : 1;
    const _estSec = Math.max(5, pageCount * 0.8);
    let _phaseLabel = 'Building…';
    setExportProgress(2);

    if (exportTimerRef.current) clearInterval(exportTimerRef.current);
    exportTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - _start) / 1000;
      const pct = Math.min(94, Math.round((elapsed / _estSec) * 100));
      setExportProgress(pct);
      updateActivity(`pdf-${reportSlug}`, { phase: _phaseLabel, pct });
    }, 1000);

    const onPhase = (phase) => {
      switch (phase) {
        case 'building':   _phaseLabel = 'Building document…'; break;
        case 'rendering':  _phaseLabel = `Rendering ${pageCount} pages…`; break;
        case 'download':   _phaseLabel = 'Writing file…'; break;
        case 'ready': {
          clearInterval(exportTimerRef.current); exportTimerRef.current = null;
          setExportProgress(100);
          notify.success(`${dbName} - ${reportDisplayName} Report - PDF - Finished`, 5000);
          completeActivity(`pdf-${reportSlug}`);
          break;
        }
        case 'cancelled':
          clearInterval(exportTimerRef.current); exportTimerRef.current = null;
          setExportProgress(0);
          notify.info(`${dbName} - ${reportDisplayName} Report - PDF - Cancelled`, 3000);
          failActivity(`pdf-${reportSlug}`);
          break;
        default: break;
      }
    };

    try {
      // All reports: 40 rows/page
      const effectiveRowsPerPage = 40;

      // Phase handler that defers the success/complete signals during multi-file loops
      const onPhaseSilent = (phase) => {
        switch (phase) {
          case 'building':  _phaseLabel = 'Building document…'; break;
          case 'rendering': _phaseLabel = `Rendering pages…`; break;
          case 'download':  _phaseLabel = 'Writing file…'; break;
          case 'ready':     break; // deferred — handled after loop
          case 'cancelled':
            clearInterval(exportTimerRef.current); exportTimerRef.current = null;
            setExportProgress(0);
            notify.info(`${dbName} - ${reportDisplayName} Report - PDF - Cancelled`, 3000);
            failActivity(`pdf-${reportSlug}`);
            break;
          default: break;
        }
      };

      if (exportGroupBy && exportGroupSeparate) {
        // Separate PDF file per unique group value — auto-download each
        const groupMap = {};
        sortedData.forEach(row => {
          const cf = toCamelCasePdf(exportGroupBy);
          const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
          const key = val != null ? String(val) : '(Blank)';
          if (!groupMap[key]) groupMap[key] = [];
          groupMap[key].push(row);
        });
        for (const [groupVal, groupRows] of Object.entries(groupMap).sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })) {
          if (cancelExportRef.current) break;
          // For Index reports: each separate file gets columns with that group's dynamic spec labels
          const groupSpecificCols = columnsByGroup?.[groupVal] || cols;
          const result = await exportReportPdf({
            config: { ...pdfHeaderConfig, reportName: `${reportDisplayName} - ${groupVal}` },
            columns: groupSpecificCols,
            columnWidths: colWidths,
            data: groupRows,
            parameters,
            paramHeaderWidths,
            logoSrc: wmLogo,
            clientLogoSrc: clientLogoSrc || undefined,
            clientName: clientName || undefined,
            sortOrder: sortOrder.length > 0 ? sortOrder : undefined,
            groupByField: exportGroupBy, // still passed so header subtitle renders per page
            rowsPerPage: effectiveRowsPerPage,
            fontSizeOverride: reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 6 : undefined,
            qrDataUrl: (showQrCode && qrDataUrl) || undefined,
            dawnVersion: dawnVersion || undefined,
            autoDownload: true,
            isCancelled: () => cancelExportRef.current,
            onWorkerReady: (worker, reject) => { activeWorkerRef.current = { worker, reject }; },
            onPhase: onPhaseSilent,
            onProgress: (msg) => setExportingType(msg ? 'pdf' : null)
          });
          activeWorkerRef.current = null;
          if (result === false || cancelExportRef.current) break;
        }
        if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
        if (!cancelExportRef.current) {
          setExportProgress(100);
          notify.success(`${dbName} - ${reportDisplayName} Report - PDF - Finished`, 5000);
          completeActivity(`pdf-${reportSlug}`);
        }
      } else {
        const groupedLabel = (exportGroupBy && !exportGroupSeparate)
          ? (config?.parameters?.find(p => p.field === exportGroupBy)?.label
              || config?.columns?.find(c => c.field === exportGroupBy)?.label
              || exportGroupBy)
          : undefined;
        const result = await exportReportPdf({
          config: { ...pdfHeaderConfig, reportName: reportDisplayName },
          columns: cols,
          columnWidths: colWidths,
          data: sortedData,
          parameters,
          paramHeaderWidths,
          logoSrc: wmLogo,
          clientLogoSrc: clientLogoSrc || undefined,
          clientName: clientName || undefined,
          sortOrder: sortOrder.length > 0 ? sortOrder : undefined,
          groupByField: exportGroupBy || undefined,
          groupByLabel: groupedLabel,
          rowsPerPage: effectiveRowsPerPage,
          fontSizeOverride: reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 6 : undefined,
          qrDataUrl: (showQrCode && qrDataUrl) || undefined,
          dawnVersion: dawnVersion || undefined,
          columnsByGroup: columnsByGroup || undefined,
          isCancelled: () => cancelExportRef.current,
          onWorkerReady: (worker, reject) => { activeWorkerRef.current = { worker, reject }; },
          onPhase,
          onProgress: (msg) => setExportingType(msg ? 'pdf' : null)
        });
        activeWorkerRef.current = null;
        if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
        // Exporter returns false when user cancels the save dialog — treat as cancellation
        if (result === false) {
          setExportingType(null);
          setExportProgress(0);
          failActivity(`pdf-${reportSlug}`);
        }
      }
    } catch (err) {
      if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
      console.error('PDF export failed:', err);
      setExportingType(null);
      setExportProgress(0);
      failActivity(`pdf-${reportSlug}`);
    }
  }, [config, sortedData, sortOrder, parameters, paramHeaderWidths, exportGroupBy, exportGroupSeparate, reportSlug, dbSlug, notify, dbName, reportDisplayName, clientLogoSrc, clientName, showQrCode, qrDataUrl, dawnVersion, registerActivity, updateActivity, completeActivity, failActivity]);

  // Excel export
  const handleExportExcel = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    cancelExportRef.current = false;
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    const actId = `excel-${reportSlug}`;
    registerActivity(actId, {
      name: `${dbName} - ${reportDisplayName} Report`, type: 'download', format: 'excel',
      phase: 'Generating Excel…', pct: 0,
      restartFn: () => handleExportExcel(),
      exportRecipe: { reportId: reportSlug, reportName: reportDisplayName, reportPath: `/reports/${reportSlug}`, format: 'excel', filterParams: parameters, fileName: buildReportFilename(reportDisplayName, 'xlsx'), dbSlug: dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa', dbName },
    });
    notify.status(`${dbName} - ${reportDisplayName} Report - Excel - Started`, 0);
    try {
      if (exportGroupBy && exportGroupSeparate) {
        // Separate file per group — auto-download each
        const groupMap = {};
        sortedData.forEach(row => {
          const cf = toCamelCase(exportGroupBy);
          const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
          const key = val != null ? String(val) : '(Blank)';
          if (!groupMap[key]) groupMap[key] = [];
          groupMap[key].push(row);
        });
        for (const [groupVal, groupRows] of Object.entries(groupMap).sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })) {
          if (cancelExportRef.current) break;
          await exportReportExcel({
            config: { ...config, reportName: `${reportDisplayName} - ${groupVal}` },
            columns: cols, data: groupRows, parameters,
            autoDownload: true,
            isCancelled: () => cancelExportRef.current,
            onProgress: (msg) => setExportingType(msg ? 'excel' : null)
          });
        }
      } else {
        await exportReportExcel({
          config,
          columns: cols,
          data: sortedData,
          parameters,
          groupByField: exportGroupBy || undefined,
          isCancelled: () => cancelExportRef.current,
          onProgress: (msg) => setExportingType(msg ? 'excel' : null)
        });
      }
      notify.success(`${dbName} - ${reportDisplayName} Report - Excel - Finished`, 5000);
      completeActivity(actId);
    } catch (err) {
      console.error('Excel export failed:', err);
      notify.info(`${dbName} - ${reportDisplayName} Report - Excel - Cancelled`, 3000);
      setExportingType(null);
      failActivity(actId);
    }
  }, [config, sortedData, parameters, exportGroupBy, exportGroupSeparate, reportSlug, dbSlug, notify, dbName, reportDisplayName, registerActivity, completeActivity, failActivity]);

  // CSV export
  const handleExportCsv = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    cancelExportRef.current = false;
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    const actId = `csv-${reportSlug}`;
    registerActivity(actId, {
      name: `${dbName} - ${reportDisplayName} Report`, type: 'download', format: 'csv',
      phase: 'Generating CSV…', pct: 0,
      restartFn: () => handleExportCsv(),
      exportRecipe: { reportId: reportSlug, reportName: reportDisplayName, reportPath: `/reports/${reportSlug}`, format: 'csv', filterParams: parameters, fileName: buildReportFilename(reportDisplayName, 'csv'), dbSlug: dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa', dbName },
    });
    notify.status(`${dbName} - ${reportDisplayName} Report - CSV - Started`, 0);
    try {
      if (exportGroupBy && exportGroupSeparate) {
        const groupMap = {};
        sortedData.forEach(row => {
          const cf = toCamelCase(exportGroupBy);
          const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
          const key = val != null ? String(val) : '(Blank)';
          if (!groupMap[key]) groupMap[key] = [];
          groupMap[key].push(row);
        });
        for (const [groupVal, groupRows] of Object.entries(groupMap).sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })) {
          if (cancelExportRef.current) break;
          await exportReportCsv({
            config: { ...config, reportName: `${reportDisplayName} - ${groupVal}` },
            columns: cols, data: groupRows,
            autoDownload: true,
            isCancelled: () => cancelExportRef.current,
            onProgress: (msg) => setExportingType(msg ? 'csv' : null)
          });
        }
      } else {
        await exportReportCsv({
          config,
          columns: cols,
          data: sortedData,
          groupByField: exportGroupBy || undefined,
          isCancelled: () => cancelExportRef.current,
          onProgress: (msg) => setExportingType(msg ? 'csv' : null)
        });
      }
      notify.success(`${dbName} - ${reportDisplayName} Report - CSV - Finished`, 5000);
      completeActivity(actId);
    } catch (err) {
      console.error('CSV export failed:', err);
      notify.info(`${dbName} - ${reportDisplayName} Report - CSV - Cancelled`, 3000);
      setExportingType(null);
      failActivity(actId);
    }
  }, [config, sortedData, parameters, exportGroupBy, exportGroupSeparate, reportSlug, dbSlug, notify, dbName, reportDisplayName, registerActivity, completeActivity, failActivity]);

  if (configLoading || !paramOptionsReady) {
    return <div className="page-container"><p>Loading report configuration...</p></div>;
  }

  if (!config) {
    return <div className="page-container"><p>Report not found: {reportSlug}</p></div>;
  }

  const columns = [...(config.columns || [])].sort((a, b) => a.order - b.order);
  const isIndexReport = !!FIXED_GROUP_BY[reportSlug];
  // Preview/PDF columns with hidden fields removed (Index reports only)
  const displayColumns = isIndexReport
    ? columns.filter(c => !INDEX_REPORT_HIDDEN_FIELDS.has(c.field))
    : columns;
  // Header columns for the current preview page — spec labels replaced from the current group's first row
  const currentPageHeaderColumns = isIndexReport && paginatedData.length > 0
    ? applyIndexSpecLabels(displayColumns, paginatedData[0])
    : displayColumns;
  // Label for the locked group-by field (used in toolbar)
  const exportGroupByLabel = FIXED_GROUP_BY[reportSlug]
    ? (columns.find(c => c.field === FIXED_GROUP_BY[reportSlug])?.label || FIXED_GROUP_BY[reportSlug])
    : null;

  // Build sort lookup for column headers
  const sortLookup = {};
  sortOrder.forEach((s, i) => {
    sortLookup[s.field] = { direction: s.direction, priority: i + 1 };
  });

  return (
    <div className="page-container" style={{ padding: '20px' }}>
      {/* Report Title — matches dev-module-header styling */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        backgroundColor: 'var(--color-bg-white)',
        borderBottom: '1px solid var(--color-gray-200)',
        marginBottom: '0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
          <h1 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--color-primary)' }}>
            <FileSpreadsheet size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            {reportDisplayName}
          </h1>
          {config.description && (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
              {config.description}
            </span>
          )}
        </div>
      </div>

      {/* ===== Section 1: Input Parameters ===== */}
      {config.parameters && config.parameters.length > 0 && (
        <div className="import-step-panel" style={{ marginTop: 'var(--spacing-lg)' }}>
          <div
            className="import-step-header"
            onClick={() => setSec1Collapsed(c => !c)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <span className="import-step-number">1</span>
            <h3>Input Parameters</h3>
            <span style={{ marginLeft: 'auto', color: 'var(--color-gray-400)' }}>
              {sec1Collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            </span>
          </div>
          {!sec1Collapsed && <p className="import-step-desc">Select filter values to narrow report results.</p>}
          {!sec1Collapsed && <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {config.parameters.map(param => {
              const sels = parameters[param.field] || [];
              return (
                <div key={param.field} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                  {/* Status indicator */}
                  <div style={{ paddingTop: '22px', flexShrink: 0 }}>
                    {sels.length === 0
                      ? <MinusCircle size={16} style={{ color: 'var(--color-gray-400)' }} />
                      : sels.length <= 4
                        ? <CheckCircle size={16} style={{ color: '#22a355' }} />
                        : <XCircle size={16} style={{ color: '#dc3540' }} />}
                  </div>
                  {/* Combobox input */}
                  <div style={{ flexShrink: 0 }}>
                    <ReportParameterCombobox
                      param={param}
                      selections={sels}
                      onChange={(selections) => updateParameter(param.field, selections)}
                      reportId={reportSlug}
                      activeFilters={parameters}
                      initialOptions={paramOptions[param.field] || []}
                      initialWidth={paramComboboxWidths[param.field] || 180}
                    />
                  </div>
                  {/* Applied filters with remove button */}
                  {sels.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', paddingTop: '20px' }}>
                      {sels.map((sel, i) => (
                        <span
                          key={i}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            padding: '2px 8px', borderRadius: '12px', fontSize: '12px',
                            background: sel.type === 'picked' ? '#e3edf7' : sel.type === 'special' ? '#d1fae5' : '#fef3cd',
                            color: sel.type === 'picked' ? '#1f497d' : sel.type === 'special' ? '#065f46' : '#856404',
                            border: `1px solid ${sel.type === 'picked' ? '#b8d4f0' : sel.type === 'special' ? '#6ee7b7' : '#ffc107'}`
                          }}
                        >
                          <span style={{
                            fontSize: '9px', fontWeight: 700,
                            background: sel.type === 'picked' ? '#1f497d' : sel.type === 'special' ? '#065f46' : '#856404',
                            color: '#fff', borderRadius: '50%',
                            width: '14px', height: '14px', display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center', flexShrink: 0
                          }}>
                            {i + 1}
                          </span>
                          {sel.display}
                          <span
                            onClick={() => {
                              const updated = sels.filter((_, idx) => idx !== i);
                              updateParameter(param.field, updated);
                            }}
                            style={{
                              cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
                              lineHeight: 1, marginLeft: '2px', opacity: 0.7,
                              color: sel.type === 'picked' ? '#1f497d' : sel.type === 'special' ? '#065f46' : '#856404'
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = 1}
                            onMouseLeave={(e) => e.target.style.opacity = 0.7}
                          >
                            x
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Report options row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingTop: '4px', borderTop: '1px solid var(--color-gray-200)', marginTop: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--color-gray-600)', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={showQrCode}
                  onChange={e => setShowQrCode(e.target.checked)}
                />
                Show QR Code in report header
              </label>
            </div>
          </div>}
        </div>
      )}

      {/* ===== Section 2: Formatting/Exporting Parameters ===== */}
      <div className="import-step-panel" style={{ marginTop: 'var(--spacing-lg)' }}>
        <div
          className="import-step-header"
          onClick={() => setSec2Collapsed(c => !c)}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <span className="import-step-number">2</span>
          <h3>Formatting / Exporting Parameters</h3>
          <span style={{ marginLeft: 'auto', color: 'var(--color-gray-400)' }}>
            {sec2Collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </span>
        </div>
        {!sec2Collapsed && (
          <>
            <p className="import-step-desc">Run report, adjust sorting, review details, and export results.</p>
            <ReportToolbar
              onRunReport={runReport}
              loading={loading}
              hasOverLimit={hasOverLimit}
              activeFilterCount={activeFilterCount}
              isCurrent={isCurrent}
              onClearFilters={clearAllFilters}
              isDefaultSort={isDefaultSort}
              sortOrder={sortOrder}
              defaultSort={defaultSort}
              onResetSort={resetSort}
              totalCount={totalCount}
              executionMs={executionMs}
              pageSize={pageSize}
              pageSizeLabel={config?.layout?.defaultPageSize || 'tabloid'}
              totalPages={totalPages}
              hasRun={hasRun}
              onExportPdf={hasRun && sortedData.length > 0 ? handleExportPdf : null}
              onExportExcel={hasRun && sortedData.length > 0 ? handleExportExcel : null}
              onExportCsv={hasRun && sortedData.length > 0 ? handleExportCsv : null}
              exportingType={exportingType}
              exportProgress={exportProgress}
              onCancelExport={handleCancelExport}
              columns={config?.parameters || []}
              exportGroupBy={exportGroupBy}
              onExportGroupByChange={FIXED_GROUP_BY[reportSlug] ? null : setExportGroupBy}
              exportGroupByLabel={exportGroupByLabel}
              exportGroupSeparate={exportGroupSeparate}
              onExportGroupSeparateChange={setExportGroupSeparate}
              exportGroupCount={exportGroupCount}
            />
          </>
        )}
      </div>

      {/* ===== Section 3: Report Preview ===== */}
      <div className="import-step-panel" style={{ marginTop: 'var(--spacing-lg)', overflowX: 'hidden' }}>
        <div
          className="import-step-header"
          onClick={() => setSec3Collapsed(c => !c)}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <span className="import-step-number">3</span>
          <h3>Report Preview</h3>
          <span style={{ marginLeft: 'auto', color: 'var(--color-gray-400)' }}>
            {sec3Collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </span>
        </div>

      {!sec3Collapsed && <>
      <p className="import-step-desc">Preview matches the PDF export layout. Click column headers to sort. Shift+click for multi-column sort.</p>
      {/* Pre-run placeholder — no scale wrapper, no reflow risk */}
      {!hasRun && !loading && (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#999' }}>
          <p style={{ fontSize: '16px' }}>Set parameters and click <strong>Run Report</strong> to view data.</p>
        </div>
      )}

      {/* Scaled preview — only rendered after first run */}
      {hasRun && (
      <>
      <div ref={previewContainerRef} style={{ overflow: 'hidden' }}>
      <div
        ref={previewContentRef}
        style={{
          transformOrigin: 'top left',
          transform: `scale(${previewScale})`,
          width: '1632px', // TABLOID landscape: 1224pt × (96/72) px/pt
          marginBottom: previewScale < 1 ? `${(previewScale - 1) * (previewContentRef.current?.offsetHeight ?? 0)}px` : 0
        }}
      >

      {/* Report Page Header — mirrors PDF output exactly */}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        position: 'relative',
        padding: '10px 32px', // 32px ≈ 24pt PDF horizontal padding × (96/72)
        borderBottom: '2px solid #1a5568', // matches PDF headerContainer borderBottom
        background: '#fff', marginBottom: '0'
      }}>
        {/* Left: WM logo + QR code */}
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={wmLogo} alt="Wunderlich-Malec" style={{ height: '96px', width: 'auto' }} />
          {showQrCode && qrDataUrl && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
              <img
                src={qrDataUrl}
                alt="QR Code"
                title="Scan to open this report in DAWN"
                style={{ height: '86px', width: '86px', imageRendering: 'pixelated' }}
              />
              <span style={{ fontSize: '6px', color: 'var(--color-gray-500)', whiteSpace: 'nowrap' }}>Scan to open in DAWN</span>
            </div>
          )}
        </div>

        {/* Spacer to push parameters right */}
        <div style={{ flex: '1 1 auto' }} />

        {/* Center: Report Title — absolutely centered over full header width */}
        <div style={{
          position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <div style={{
            fontSize: '16px', fontWeight: 700, color: config?.branding?.titleColor || '#1f497d',
            fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
            textTransform: 'uppercase', letterSpacing: '1px'
          }}>
            {reportDisplayName}
          </div>
          {groupedPages?.[currentPage - 1]?.subtitle && (
            <div style={{
              fontSize: '13px', fontWeight: 700, color: config?.branding?.titleColor || '#1f497d',
              fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
              textTransform: 'uppercase', letterSpacing: '1px', marginTop: '1px'
            }}>
              {groupedPages[currentPage - 1].subtitle}
            </div>
          )}
          {hasRun && (
            <div style={{ fontSize: '8px', color: 'var(--color-gray-500)', marginTop: '2px' }}>
              {groupedPages
                ? `${(groupedPages[currentPage - 1]?.groupRowCount || 0).toLocaleString()} Records`
                : `${sortedData.length.toLocaleString()} Records`}
            </div>
          )}
        </div>

        {/* Right: Selected parameters — fixed 3-col × 2-row grid (6 positions).
            Params anchor to position 1 (bottom-right) and fill toward position 6 (top-left).
            Values frozen at last run time (committedParameters), not live selections. */}
        {(() => {
          const allParams = config.parameters || [];
          const params = isIndexReport
            ? allParams.filter(p => p.field !== 'Index_Sheet')
            : allParams;

          // Build 6-slot array: slots[0]=pos6 .. slots[5]=pos1
          // With n params: last n slots are filled, earlier slots are null (empty)
          const NUM_SLOTS = 6;
          const slots = Array(NUM_SLOTS).fill(null);
          params.forEach((p, i) => { slots[NUM_SLOTS - params.length + i] = p; });

          // Column c: top=slots[c*2], bottom=slots[c*2+1]
          const colWidths = [0, 1, 2].map(c =>
            Math.max(
              slots[c * 2] ? (paramHeaderWidths[slots[c * 2].field] || 80) : 0,
              slots[c * 2 + 1] ? (paramHeaderWidths[slots[c * 2 + 1].field] || 80) : 0,
              80
            )
          );

          const renderSlot = (slotIdx, colWidth) => {
            const param = slots[slotIdx];
            if (!param) return <div key={`empty-${slotIdx}`} style={{ width: `${colWidth}px`, height: '34px' }} />;
            const selsList = committedParameters[param.field] || [];
            return (
              <div key={param.field} style={{ width: `${colWidth}px`, height: '34px', overflow: 'hidden', paddingLeft: '6px', textAlign: 'left' }}>
                <div style={{ fontSize: '5px', fontWeight: 700, color: 'var(--color-gray-700)', borderBottom: '0.5px solid var(--color-gray-300)', paddingBottom: '0px', marginBottom: '1px' }}>
                  {param.label}
                </div>
                {selsList.length === 0 ? (
                  <div style={{ fontSize: '6px', color: 'var(--color-gray-400)', fontStyle: 'italic' }}>None Selected</div>
                ) : (
                  selsList.slice(0, 4).map((sel, i) => (
                    <div key={i} title={sel.fullDisplay || sel.display} style={{ fontSize: '4px', color: 'var(--color-gray-500)', lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      {sel.fullDisplay || sel.display}
                    </div>
                  ))
                )}
              </div>
            );
          };

          return (
            <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[0, 2, 4].map((si, ci) => renderSlot(si, colWidths[ci]))}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[1, 3, 5].map((si, ci) => renderSlot(si, colWidths[ci]))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Data Table */}
      {hasRun && (
        <div className="data-grid-container" style={{ flex: 'none', overflowX: 'visible' }}>
          <table className="data-grid" style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                {currentPageHeaderColumns.map(col => {
                  const sortInfo = sortLookup[col.field];
                  return (
                    <th
                      key={col.field}
                      onClick={(e) => handleSortClick(col.field, e)}
                      title={col.label}
                      style={{
                        width: previewColWidths[col.field] || `${col.pixelWidth || col.width * 10 || 80}px`,
                        textAlign: col.align || 'left',
                        padding: '4px 3px',
                        borderBottom: '2px solid var(--color-gray-300)',
                        borderRight: '1px solid var(--color-gray-300)',
                        backgroundColor: 'var(--color-gray-200)',
                        fontWeight: 700,
                        color: 'var(--color-gray-700)',
                        textTransform: 'uppercase',
                        fontSize: '7px',
                        letterSpacing: '0.3px',
                        overflow: 'hidden',
                        whiteSpace: 'normal',
                        cursor: 'pointer',
                        userSelect: 'none',
                        verticalAlign: 'top',
                        lineHeight: '1.2'
                      }}
                    >
                      {(() => {
                        const [hLine1, hLine2] = splitHeaderLabel(col.label);
                        return (
                          <>
                            <span style={{ display: 'block' }}>{hLine1}</span>
                            <span style={{ display: 'block', minHeight: '1em' }}>{hLine2}</span>
                            <span style={{ display: 'block', minHeight: '1em', fontSize: '6px', fontWeight: 400, color: 'var(--color-gray-400)', letterSpacing: 0 }}>
                              {sortInfo
                                ? `${sortInfo.priority} ${sortInfo.direction === 'asc' ? 'asc' : 'dsc'}`
                                : '\u00a0'}
                            </span>
                          </>
                        );
                      })()}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={displayColumns.length} style={{
                    padding: 'var(--spacing-xl)', textAlign: 'center',
                    color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)'
                  }}>
                    No data returned
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, idx) => (
                  <tr
                    key={row.id || idx}
                    style={{
                      backgroundColor: config.layout?.zebraStriping && idx % 2 === 1
                        ? 'var(--color-gray-100)' : 'var(--color-bg-white)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-teal-50)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor =
                      config.layout?.zebraStriping && idx % 2 === 1
                        ? 'var(--color-gray-100)' : 'var(--color-bg-white)'}
                  >
                    {displayColumns.map(col => {
                      const camelField = toCamelCase(col.field);
                      const displayKey = `${camelField}_Display`;
                      const displayValue = row[displayKey] != null ? row[displayKey] : row[camelField];

                      const isCellWrappable = col.field === 'Note' || col.field === 'Comment';
                      return (
                        <td key={col.field} style={{
                          padding: '3px 4px',
                          textAlign: isCellWrappable ? 'left' : (col.align || 'left'),
                          borderBottom: '1px solid var(--color-gray-200)',
                          borderRight: '1px solid var(--color-gray-200)',
                          fontSize: '8px',
                          fontFamily: "'Helvetica', sans-serif",
                          verticalAlign: isCellWrappable ? 'top' : 'middle',
                          overflow: 'hidden',
                          whiteSpace: isCellWrappable ? 'normal' : 'nowrap'
                        }}>
                          {displayValue != null ? String(displayValue) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Report Preview Footer — mirrors PDF footer, inside previewContentRef so width matches table */}
      {hasRun && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '4px 0px',
          borderTop: '2px solid #1a5568',
          background: '#fff',
          fontSize: '9px', color: 'var(--color-gray-500)'
        }}>
          <span>{new Date().toLocaleString()}</span>
          {(clientIconUrl || clientName) && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {clientIconUrl && <img src={clientIconUrl} alt="Client" style={{ height: '16px', width: 'auto', objectFit: 'contain' }} />}
              {clientName} Confidential
            </span>
          )}
          {dawnVersion && <span>Dawn - {dawnVersion}</span>}
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <img src={wmLogoCb} alt="Wunderlich-Malec" style={{ height: '16px', width: 'auto' }} />
            Wunderlich-Malec Confidential
          </span>
          <span style={{ textAlign: 'right' }}>{hasRun ? `Page ${currentPage} of ${totalPages}` : ''}</span>
        </div>
      )}

      </div>{/* close previewContentRef */}
      </div>{/* close previewContainerRef */}

      {/* Paginator — outside scaled area so controls stay full size */}
      {hasRun && (
        <ReportPaginator
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={sortedData.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      )}

      </>
      )}{/* close hasRun */}
      </>}{/* close !sec3Collapsed */}

      </div>{/* close Section 3 import-step-panel */}
    </div>
  );
};

export default ReportViewerPage;
